import { createSelector } from '@reduxjs/toolkit';
import { anonymousUserSelector, sessionUserSelector } from '..';

/**
 * Fetches the current anonymous user record from the session reducer, if it exists
 * @param  {any} session
 * @return {any}
 */
export const getAnonymousUser = createSelector(
  anonymousUserSelector,
  (user) => user,
);

/**
 * Get the id of the current logged-in user. If the user is impersonating another user, get the impersonated user's id
 * @param  {any} state
 * @return {string}
 */
export const getUserId = createSelector(
  sessionUserSelector,
  anonymousUserSelector,
  (user, anonymousUser) => {
    if (user) {
      const { id, impersonateId, uid } = user;

      return impersonateId || id || uid;
    }

    if (anonymousUser) {
      return anonymousUser.id || anonymousUser.uid;
    }

    return null;
  },
);

/**
 * Get the current login state of the firebase user
 * @param  {any} state
 * @return {boolean|null}
 */
export const getSessionUserState = createSelector(
  sessionUserSelector,
  anonymousUserSelector,
  (user, anonymousUser) => {
    if ((user === null || (user && (!user.publicFetched || !user.privateFetched))) && anonymousUser === null) {
      return null;
    }

    if (!user && anonymousUser === null) {
      return null;
    }

    if (!user && anonymousUser) {
      return false;
    }

    return true;
  },
);

/**
 * Determine whether or not the current user is impersonating another user
 * @param  {any} state
 */
export const userIsImpersonating = createSelector(
  sessionUserSelector,
  (user) => user && !!user.impersonateId,
);

/**
 * Check whether currently logged in user is admin.
 * @param state
 * @return {*}
 */
export const getSessionUserAdminStatus = createSelector(
  sessionUserSelector,
  (user) => (user ? user.isAdmin : false),
);
